import { useTerminalsStateRE } from "@/States/Terminals";
import { inactiveTenantsColumns } from "@/Utils/tableColumns/inactiveTenantsColumns";
import { trpc } from "@/Utils/trpc";
import { MediaQuery, useMediaQuery } from "@/Utils/useMediaQuery";
import { useMinimalCustomers } from "@/api/hooks/useCustomers/useMinimalCustomers";
import LoadingTable from "@/components/LoadingTable";
import { Table } from "@/components/Table";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import {
  OPTION_ALL,
  hasNonAllOptionActive,
} from "@/components/pageCards/filterSort/constants";
import {
  createTimeFilters,
  getTenantCategoryOptions,
} from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import {
  FilterSort,
  FilterSortMenuType,
} from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { useMemo, useState } from "react";

import { FilterTimeFrameValue } from "server/config-api/index.js";
import { GetCustomersResponse } from "server/dwhApi/customers/models.js";

export const InactiveTenantsCard = () => {
  const { t } = useTrans();
  const [
    { timeframe: [selectedTimeframe] = [], tenantCategory },
    setActiveOptions,
  ] = useState<ActiveOptions<"timeframe" | "tenantCategory">>({} as any);
  const matches = useMediaQuery(MediaQuery.ExtraSmall);

  const { terminalConfig } = useConfigService();

  const defaultPeriod = useMemo(
    () => terminalConfig?.defaultTimeFrame || FilterTimeFrameValue.Last30Days,
    [terminalConfig],
  );

  const { startTimeISO, endTimeISO } = useMemo(() => {
    return getTimeFrame(
      selectedTimeframe?.value as FilterTimeFrameValue,
      defaultPeriod,
    );
  }, [selectedTimeframe, defaultPeriod]);

  const { usedTenantCategories } = useMinimalCustomers();

  const { currentTerminal } = useTerminalsStateRE();

  const tenantCategoryValue = useMemo(() => {
    return hasNonAllOptionActive(tenantCategory)
      ? tenantCategory[0].value
      : undefined;
  }, [tenantCategory]);

  const { data: currentPeriod, isLoading: isCurrentDataLoading } =
    trpc.dwh.customers.get.useQuery(
      {
        startTimeISO,
        endTimeISO,
        commercialCustomerCategory: tenantCategoryValue,
        terminalId: currentTerminal.id,
      },
      {
        queryKeyHashFn: () =>
          [
            "customers",
            startTimeISO,
            endTimeISO,
            tenantCategoryValue,
            currentTerminal.id,
          ].toString(),
      },
    );

  const { data: sinceBeginningData, isLoading: isSinceBeginningDataLoading } =
    trpc.dwh.customers.get.useQuery(
      {
        ...getTimeFrame(FilterTimeFrameValue.SinceBeginning),
        terminalId: currentTerminal.id,
      },
      {
        queryKeyHashFn: () =>
          ["customersSinceBeginning", currentTerminal.id].toString(),
      },
    );

  const { data: accessCodes, isLoading: isLoadingAccessCodes } =
    trpc.scanner.customers.getAllAccessCodes.useQuery(
      {
        terminalId: currentTerminal.id,
      },
      {
        queryKeyHashFn: () =>
          ["customersAccessCodes", currentTerminal].toString(),
      },
    );

  const getRegistrationsForCustomer = (
    data: GetCustomersResponse | undefined,
    customerId: any,
  ) => {
    return (
      data?.customers.find(
        (el: { customerId: any }) => el.customerId === customerId,
      )?.totalOccurrences ?? 0
    );
  };

  const totalRegistrationsSinceBeginning = (customerId: string) => {
    return getRegistrationsForCustomer(sinceBeginningData, customerId);
  };

  const inactiveTenants =
    currentPeriod?.customers.filter(
      (el) => (el.totalWasteWeight.quantity ?? 0) === 0,
    ) ?? [];

  const filters: FilterSort[] = [
    createTimeFilters({ id: "timeframe", defaultValue: defaultPeriod }, [
      FilterTimeFrameValue.Today,
      FilterTimeFrameValue.LastWeek,
      FilterTimeFrameValue.Last30Days,
      FilterTimeFrameValue.YearToDate,
      FilterTimeFrameValue.Last6FullMonths,
      FilterTimeFrameValue.LastFullYear,
    ]),
    {
      options: getTenantCategoryOptions(usedTenantCategories),
      title: "statisticsLabels:tenantCategory",
      id: "tenantCategory",
      type: "filter",
      menuType: FilterSortMenuType.Select,
      defaultValue: OPTION_ALL,
    },
  ];

  const isLoading =
    isCurrentDataLoading || isSinceBeginningDataLoading || isLoadingAccessCodes;

  const dataWithTotalRegistrations = inactiveTenants.map((el) => ({
    ...el,
    previousRegistrations: t(
      totalRegistrationsSinceBeginning(el.customerId)
        ? "common:yes"
        : "common:no",
    ),
    tenantCode:
      accessCodes?.data.find((d) => d.storeUuid === el.customerId)
        ?.accessCode || "",
  }));

  return (
    <Card
      title={"statisticsLabels:inactiveTenants"}
      filters={filters}
      onFilterOptionChange={setActiveOptions}
      filterLocation={CardComponentFilterLocation.RIGHT}
      className="xl:col-start-2"
    >
      <p className="mb-8">{t("hints:inactiveTenantsInfo")}</p>

      {isLoading ? (
        <LoadingTable rows={10} />
      ) : (
        <Table
          columns={inactiveTenantsColumns(matches)}
          data={dataWithTotalRegistrations}
          className="max-h-96 overflow-auto"
          sortingHeader="previousRegistrations"
          emptyViewType="noInactiveTenants"
        />
      )}
    </Card>
  );
};
